import { memo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';

import { formatPercent } from '../../util/format';
import { t as translate } from '../../i18n';

/**
 * StatCard - Displays a metric with optional trend indicator
 *
 * Memoized to prevent unnecessary re-renders in lists.
 *
 * @param {string} title - The label for the stat
 * @param {string|number} value - The main value to display
 * @param {string} subtitle - Optional subtitle text
 * @param {number} trend - Percentage change (positive = up, negative = down)
 * @param {string} trendLabel - Label for trend (e.g., "vs last month")
 * @param {string} icon - Feather icon name
 * @param {string} color - Accent color for the card
 */
const StatCard = memo(function StatCard({
  title,
  value,
  subtitle,
  trend,
  trendLabel = translate('analytics:vsPreviousPeriod'),
  icon,
  color = '#FFC451',
  style,
}) {
  const trendColor = trend > 0 ? '#22c55e' : trend < 0 ? '#ef4444' : '#6b7280';
  const trendIcon = trend > 0 ? 'trending-up' : trend < 0 ? 'trending-down' : 'minus';

  return (
    <View style={[styles.card, style]}>
      <View style={styles.header}>
        {icon && (
          <View style={[styles.iconContainer, { backgroundColor: `${color}20` }]}>
            <Feather name={icon} size={20} color={color} />
          </View>
        )}
        <Text style={styles.title}>{title}</Text>
      </View>

      <Text style={styles.value}>{value}</Text>

      {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}

      {trend !== undefined && trend !== null && (
        <View style={styles.trendContainer}>
          <View style={[styles.trendBadge, { backgroundColor: `${trendColor}15` }]}>
            <Feather name={trendIcon} size={14} color={trendColor} />
            <Text style={[styles.trendText, { color: trendColor }]}>
              {formatPercent(Math.abs(trend), 1)}
            </Text>
          </View>
          <Text style={styles.trendLabel}>{trendLabel}</Text>
        </View>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  // V3: Card styling matches Submit Report cards
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#F0F0F0',
    overflow: 'hidden',
    justifyContent: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  iconContainer: {
    width: 36,
    height: 36,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  title: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6b7280',
    flexShrink: 1,
    textAlign: 'center',
  },
  value: {
    fontSize: 32,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 12,
    color: '#9ca3af',
    marginBottom: 8,
    textAlign: 'center',
  },
  trendContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  trendBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 8,
  },
  trendText: {
    fontSize: 13,
    fontWeight: '600',
    marginLeft: 4,
  },
  trendLabel: {
    fontSize: 12,
    color: '#9ca3af',
  },
});

export default StatCard;
